import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Button, Dropdown, Modal, showSnackbar, TextInput } from 'fluxo-ui';
import { ArchivedIcon, CheckCircleIcon, CopyIcon, EditIcon, ListIcon, PlayIcon, PlusIcon, SearchIcon, TrashIcon } from 'fluxo-ui/icons';

import { EXAM_STATUS, EXAM_TYPES, ROUTES } from '../common/constants';
import { Badge, Card, EmptyState, PageHeader } from '../components/common';
import examService from '../services/exam.service';
import { useExamStore } from '../store/examStore';

import type { BadgeTone } from '../components/common/Badge';

const SEARCH_DEBOUNCE_MS = 350;

const statusTones: Record<string, BadgeTone> = {
    active: 'success',
    draft: 'neutral',
    scheduled: 'warning',
    completed: 'accent',
    archived: 'danger',
};

const statusAccents: Record<string, string> = {
    active: 'border-l-emerald-500',
    scheduled: 'border-l-amber-400',
    completed: 'border-l-teal-500',
    archived: 'border-l-red-400',
    draft: 'border-l-slate-300 dark:border-l-slate-600',
};

const ExamListPage: React.FC = () => {
    const navigate = useNavigate();
    const { exams, total, loading, fetchExams, deleteExam, publishExam, archiveExam, unarchiveExam } = useExamStore();

    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(20);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [examToDelete, setExamToDelete] = useState<string | null>(null);

    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

    const refetch = () => {
        const params: any = {
            page,
            limit: rowsPerPage,
        };
        if (searchQuery) params.search = searchQuery;
        if (selectedType) params.type = selectedType;
        if (selectedStatus) params.status = selectedStatus;
        fetchExams(params);
    };

    useEffect(() => {
        const timer = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, searchQuery, selectedType, selectedStatus, fetchExams]);

    // Reset to the first page when filters change
    useEffect(() => {
        setPage(1);
    }, [searchQuery, selectedType, selectedStatus]);

    const handleDelete = async () => {
        if (!examToDelete) return;

        try {
            await deleteExam(examToDelete);
            showSnackbar('Exam deleted successfully', 'Success', { type: 'success' });
            setDeleteModalOpen(false);
            setExamToDelete(null);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to delete exam', 'Error', { type: 'error' });
        }
    };

    const handlePublish = async (examId: string) => {
        try {
            await publishExam(examId);
            showSnackbar('Exam published — participants can now take it', 'Published', { type: 'success' });
            refetch();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to publish exam', 'Error', { type: 'error' });
        }
    };

    const handleClone = async (examId: string) => {
        try {
            const cloned = await examService.cloneExam(examId);
            showSnackbar('Exam cloned as a new draft', 'Cloned', { type: 'success' });
            navigate(`/exams/${cloned._id}/edit`);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to clone exam', 'Error', { type: 'error' });
        }
    };

    const handleArchiveToggle = async (exam: any) => {
        try {
            if (exam.status === 'archived') {
                await unarchiveExam(exam._id);
                showSnackbar('Exam restored', 'Success', { type: 'success' });
            } else {
                await archiveExam(exam._id);
                showSnackbar('Exam archived', 'Success', { type: 'success' });
            }
            refetch();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to update exam', 'Error', { type: 'error' });
        }
    };

    const examTypeOptions = Object.entries(EXAM_TYPES).map(([key, value]) => ({
        label: key.replace(/_/g, ' ').toUpperCase(),
        value,
    }));

    const examStatusOptions = Object.entries(EXAM_STATUS).map(([key, value]) => ({
        label: key.replace(/_/g, ' ').toUpperCase(),
        value,
    }));

    return (
        <div className="space-y-6">
            <PageHeader
                title="Exams"
                description="Manage and create your exams"
                className="mb-0"
                actions={<Button label="Create Exam" variant="primary" leftIcon={PlusIcon} onClick={() => navigate(ROUTES.EXAM_CREATE)} />}
            />

            <Card padding="sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <TextInput
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.value)}
                        placeholder="Search exams..."
                        leftIcon={<SearchIcon />}
                    />
                    <Dropdown
                        options={[{ label: 'All Types', value: null }, ...examTypeOptions]}
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.value)}
                        placeholder="Filter by type"
                        showClear
                    />
                    <Dropdown
                        options={[{ label: 'All Statuses', value: null }, ...examStatusOptions]}
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.value)}
                        placeholder="Filter by status"
                        showClear
                    />
                </div>
            </Card>

            {/* Exams List */}
            <div>
                {loading ? (
                    <Card>
                        <div className="text-center py-10 text-slate-500 dark:text-slate-400">Loading exams...</div>
                    </Card>
                ) : exams.length === 0 ? (
                    <Card>
                        <EmptyState
                            icon={ListIcon}
                            title="No exams found"
                            description="Create your first exam to get started, or adjust your filters."
                            action={
                                <Button
                                    label="Create Exam"
                                    variant="primary"
                                    leftIcon={PlusIcon}
                                    onClick={() => navigate(ROUTES.EXAM_CREATE)}
                                />
                            }
                        />
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {exams.map((exam: any) => (
                            <div
                                key={exam._id}
                                className={`bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 border-l-4 ${statusAccents[exam.status] || statusAccents.draft} hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm transition-all p-4 cursor-pointer`}
                                onClick={() => navigate(`/exams/${exam._id}/edit`)}
                            >
                                <div className="flex justify-between items-start gap-4">
                                    <div className="min-w-0">
                                        <h3 className="font-semibold text-slate-900 dark:text-white truncate">{exam.title}</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{exam.description}</p>
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            <Badge tone="accent">{exam.type?.replace(/_/g, ' ')}</Badge>
                                            <Badge tone={statusTones[exam.status] || 'neutral'} dot>
                                                {exam.status?.replace(/_/g, ' ')}
                                            </Badge>
                                            <Badge tone="neutral">
                                                {exam.questions?.length || 0} question{(exam.questions?.length || 0) !== 1 ? 's' : ''}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div
                                        className="flex items-center gap-1 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-1.5 py-1"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {exam.status === 'draft' && (
                                            <Button
                                                leftIcon={CheckCircleIcon}
                                                layout="plain"
                                                size="sm"
                                                variant="success"
                                                onClick={() => handlePublish(exam._id)}
                                                title="Publish exam"
                                                ariaLabel="Publish exam"
                                            />
                                        )}
                                        {exam.status === 'active' && (
                                            <Button
                                                leftIcon={PlayIcon}
                                                layout="plain"
                                                size="sm"
                                                onClick={() => navigate(`/take-exam/${exam._id}`)}
                                                title="Take exam"
                                                ariaLabel="Take exam"
                                            />
                                        )}
                                        <Button
                                            leftIcon={EditIcon}
                                            layout="plain"
                                            size="sm"
                                            onClick={() => navigate(`/exams/${exam._id}/edit`)}
                                            title="Edit exam"
                                            ariaLabel="Edit exam"
                                        />
                                        <Button
                                            leftIcon={CopyIcon}
                                            layout="plain"
                                            size="sm"
                                            onClick={() => handleClone(exam._id)}
                                            title="Clone exam"
                                            ariaLabel="Clone exam"
                                        />
                                        <Button
                                            leftIcon={ArchivedIcon}
                                            layout="plain"
                                            size="sm"
                                            onClick={() => handleArchiveToggle(exam)}
                                            title={exam.status === 'archived' ? 'Restore exam' : 'Archive exam'}
                                            ariaLabel={exam.status === 'archived' ? 'Restore exam' : 'Archive exam'}
                                        />
                                        <Button
                                            leftIcon={TrashIcon}
                                            layout="plain"
                                            size="sm"
                                            variant="danger"
                                            onClick={() => {
                                                setExamToDelete(exam._id);
                                                setDeleteModalOpen(true);
                                            }}
                                            title="Delete exam"
                                            ariaLabel="Delete exam"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Pagination */}
                        <div className="flex items-center justify-between mt-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm px-4 py-3">
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                                Page <span className="font-medium text-slate-900 dark:text-white">{page}</span> of{' '}
                                <span className="font-medium text-slate-900 dark:text-white">{totalPages}</span> &middot; {total} exam
                                {total !== 1 ? 's' : ''}
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    label="Previous"
                                    size="sm"
                                    variant="default"
                                    layout="outlined"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                />
                                <Button
                                    label="Next"
                                    size="sm"
                                    variant="default"
                                    layout="outlined"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete Exam" size="sm">
                <div className="space-y-4">
                    <p className="text-slate-600 dark:text-slate-400">
                        Are you sure you want to delete this exam? This action cannot be undone and will delete all associated attempts and
                        results.
                    </p>
                    <div className="flex gap-3 justify-end">
                        <Button label="Cancel" variant="default" layout="outlined" onClick={() => setDeleteModalOpen(false)} />
                        <Button label="Delete" variant="danger" onClick={handleDelete} />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ExamListPage;
